import React, { useMemo, useRef, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from "react-router-dom";
import { v4 as uuidv4, validate as uuidValidate } from 'uuid';
import { QRCode } from "react-qrcode-logo";
import ReactToPrint from "react-to-print";
import { saveAs } from 'file-saver';
import { Button, Card, Col, Container, Form, Row, Accordion } from 'react-bootstrap';

import { encryptMessage } from '../../helpers/common';
import { SliderControl } from '../widgets/Notification';
import { getQRSeceretKeys } from '../../actions/auth';

export const QRGenerator = () => {
    const history = useHistory();
    const dispatch = useDispatch();
    const farmId = useSelector(state => state.farm.currentFarm.farm_uuid);
    const QRCryptoKey = useSelector(state => state.auth.qrSeceretKeys);

    const [isInputNumber, setIsNumber] = useState(false);
    const [deviceIds, setDeviceIds] = useState('');
    const [QRitems, setQRItems] = useState([]);
    const [isDisabled, setDisabled] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [slider, setSilder] = useState({
        QRSize: {
            value: 1,
            enabled: false
        },
        printSize: {
            value: 1,
            enabled: false
        },
        columnSize: {
            value: 1,
            enabled: false
        }
    });
    const componentRef = useRef();
    const pageStyle = `
    @page{
        padding: 10px;
    }`
    let QRDatas = [];

    useEffect(() => {
        dispatch(getQRSeceretKeys());
    }, [])

    const QRData = (data) => {
        const { v1 } = QRCryptoKey;
        let QRValue = (typeof data === 'object') ? JSON.stringify(data) : data.toString();
        const enCryptValue = encryptMessage(QRValue, v1);
        return `v1:${enCryptValue}`;
    }

    const getGridTemplateSize = () => {
        let size;
        switch (slider.QRSize.value) {
            case 1:
                size = 130;
                break;
            case 3:
                size = 150;
                break;
            case 5:
                size = 170;
                break;
            case 7:
                size = 190;
                break;
            default:
                break;
        }
        return size;
    }

    const getGridGap = () => {
        let size;
        switch (slider.columnSize.value) {
            case 1:
                size = 10;
                break;
            case 2:
                size = 15;
                break;
            case 3:
                size = 20;
                break;
            case 4:
                size = 25;
                break;
            case 5:
                size = 30;
                break;
            case 6:
                size = 35;
                break;
            case 7:
                size = 40;
                break;

            default:
                size = 10;
                break;
        }
        return size;
    }

    const getGridStyle = () => {
        let styleObj = {
            textAlign: 'center',
            display: 'grid',
            gridTemplateColumns: `repeat(${slider.printSize.value}, ${getGridTemplateSize()}px)`,
            gridGap: `${getGridGap()}px`,
            padding: '10px'
        }
        if (slider.printSize.value <= 4) styleObj['justifyContent'] = 'center';
        return styleObj;
    }

    const onUuidChange = (value) => {
        if (value) {
            const inputValue = Number(value);
            const isNumber = Number.isInteger(inputValue);
            setIsNumber(isNumber);
            if (isNumber) {
                setDeviceIds(inputValue > 0 ? value : '');
            } else {
                setDeviceIds(value);
            }
        } else {
            setDeviceIds('');
            setQRItems([]);
        }
    }

    const onGenerateQR = () => {
        if (isInputNumber && deviceIds) {
            const deviceLength = (Number(deviceIds) > 100 ? 100 : Number(deviceIds))
            setQRItems(Array.from({ length: deviceLength }, (_, index) => index + 1));
        }
        else
            if (deviceIds) {
                const ids = deviceIds.split(',');
                if (ids && ids.length > 0) {
                    let newIds = [];
                    ids.forEach((e) => {
                        const id = e.trim();
                        if (uuidValidate(id)) newIds.push(id);
                    })
                    if (newIds && newIds.length > 0) setQRItems([...new Set(newIds)]);
                    else setQRItems([]);
                }
            }
    }

    useMemo(() => {
        let enabled = (QRitems && QRitems.length > 0) ? true : false;
        setSilder({
            ...slider,
            QRSize: {
                ...slider.QRSize,
                enabled
            },
            printSize: {
                ...slider.printSize,
                enabled
            },
            columnSize: {
                ...slider.columnSize,
                enabled
            }
        })
    }, [QRitems])

    useEffect(() => {
        if (QRDatas && QRDatas.length > 0) {
            QRDatas.forEach((e) => {
                const canvas = document.getElementById(e.refId);
                if (canvas) {
                    const base64Image = canvas.toDataURL(); // Convert canvas to base64
                    e['imgUrl'] = base64Image //.split(',')[1];
                }
            });
            setDisabled(false);
        }
        else {
            setDisabled(true);
        }
    }, [QRDatas])

    const generateQRCodeImage = (value, i) => {

        QRDatas.push(...QRDatas, { id: value, refId: `QR-${i}`, encId: QRData(value) });
        const uniqueIds = [];
        const uniqueData = QRDatas.filter(element => {
            const isDuplicate = uniqueIds.includes(element.id);

            if (!isDuplicate) {
                uniqueIds.push(element.id);
                return true;
            }
            return false;
        });
        QRDatas = uniqueData;

        return (
            <QRCode
                value={QRData(value)}
                size={((slider.QRSize.value * 10) + 100)}
                enableCORS={true}
                qrStyle="dots"
                eyeRadius={0}
                eyeColor={['#4b4d4d', '#4b4d4d', '#4b4d4d']}
                id={`QR-${i}`}
            />
        );
    }

    const generateExcelFile = () => {

        let csvContent = "UUID,QR Value\n";
        for (let i = 1; i <= QRDatas.length; i++) {
            csvContent += `${QRDatas[i - 1].id},${QRDatas[i - 1].encId}\n`;
        }
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        saveAs(blob, `qr_code_${Date.now().toString()}.csv`);

        //for image export in excel file
        /*  if (type === 'xl') {
             const workbook = new ExcelJS.Workbook();
             const worksheet = workbook.addWorksheet('QR Code');
             const headerStyle = { bold: true };
 
             // Column
             const columnA = worksheet.getColumn('A');
             columnA.header = 'UUID'
             columnA.width = 40;
             columnA.alignment = { vertical: 'middle', horizontal: 'center' };
 
             const columnB = worksheet.getColumn('B');
             columnB.width = 20;
             columnB.alignment = { vertical: 'middle', horizontal: 'center' };
 
             // Header
             const UUIDHeader = worksheet.getCell('A1');
             UUIDHeader.value = 'UUID';
             UUIDHeader.style = headerStyle;
 
             const QRHeader = worksheet.getCell('B1');
             QRHeader.value = 'QR Code';
             QRHeader.style = headerStyle;
 
 
             for (let i = 1; i <= QRDatas.length; i++) {
 
                 const idCell = worksheet.getCell(`A${i + 1}`);
                 idCell.value = QRDatas[i - 1].id;
 
                 const imageId = workbook.addImage({
                     base64: QRDatas[i - 1].imgUrl,
                     extension: 'png',
                 });
 
                 worksheet.addImage(imageId, {
                     tl: { col: 1, row: i },
                     ext: { width: 100, height: 100 },
                     editAs: 'absolute'
                 });
             }
 
             worksheet.eachRow((row, i) => {
                 if (i > 1)
                     row.height = 80;
                 else
                     row.height = 20;
             });
 
             worksheet.views = [
                 {
                     state: 'frozen',
                     ySplit: 1, // Number of rows to freeze (header row)
                 },
             ];
 
             workbook.xlsx.writeBuffer().then((buffer) => {
                 const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                 saveAs(blob, `qr_code_${Date.now().toString()}.xlsx`);
             });
         } */
    };

    return (
        <Row>
            <Col xs={12}>
                <div className='content-inner qr-generator'>
                    <Card style={{ height: "90vh" }} className='animate animate__zoomIn'>
                        <Card.Header>
                            <Row>
                                <Col>
                                    <Card.Title>QR Generator</Card.Title>
                                </Col>

                                <Col>
                                    <Button
                                        className='btn btn-sm float-right'
                                        variant='danger'
                                        onClick={() => {
                                            const url = `/home/farm/${farmId}/map`;
                                            history.push(url);
                                        }}>
                                        <i className='fa fa-times-circle mr-1' />
                                        Cancel
                                    </Button>
                                </Col>
                            </Row>
                        </Card.Header>
                        <Card.Body style={{ height: 'fit-content' }} className='p-0'>
                            <Container className='p-0 m-0 w-100 h-100' style={{ maxWidth: '100%' }}>
                                <Row className='m-0 h-100'>
                                    <Col xl={9} lg={8} md={7} className='border-right h-100 scroll-bar-5 text-center py-2' style={{
                                        overflow: "auto", height: "auto", backgroundColor: '#999999'
                                    }}>
                                        <div className='paper-layout scroll-bar-5'>
                                            {QRitems.length > 0 && <div ref={componentRef} style={getGridStyle()}>
                                                {QRitems.map((id, i) => {
                                                    let value = (isInputNumber) ? uuidv4() : id;
                                                    return (
                                                        <div title={value} key={`QR-${i}`}>
                                                            {generateQRCodeImage(value, i)}
                                                        </div>
                                                    )
                                                })}
                                            </div>}
                                        </div>
                                    </Col>
                                    <Col xl={3} lg={4} md={5} className='h-100 p-4  scroll-bar-5' style={{ backgroundColor: '#FFFFFF' }} >
                                        <Form.Group name="deviceIds" >
                                            <Form.Control as="textarea" placeholder='Enter a UUID / Count of QRs needed' value={deviceIds} onChange={input => onUuidChange(input.target.value)}
                                                title="1.Enter a UUID V4 with comma separated.&#10;2.QR will generate for only valid UUID's." />
                                        </Form.Group>
                                        <Button className={`w-100 mt-3 btn btn-warning ${!deviceIds ? 'in-active__cursor' : 'active__cursor'}`} disabled={(deviceIds) ? false : true} onClick={() => { onGenerateQR() }}>Generate QR</Button>
                                        {!isDisabled && <>
                                            <hr></hr>
                                            <Accordion>
                                                <Card>
                                                    <Accordion.Toggle as={Card.Header} eventKey="0" style={{ padding: '7px' }} className='cursor-pointer ' onClick={() => { setIsOpen(!isOpen) }}>
                                                        <Row className='m-0 align-items-center'>
                                                            <div className='mr-auto ml-2'>
                                                                <p className='mb-0 font-weight-bold'>Advanced Options</p>
                                                            </div>
                                                            <div className='text-center' style={{ fontSize: '1.5rem' }}>
                                                                <span className="mt-2 mr-2 text-muted">{isOpen ? <i className="fa fa-angle-up"></i> : <i className="fa fa-angle-down"></i>}</span>
                                                            </div>
                                                        </Row>
                                                    </Accordion.Toggle>
                                                    <Accordion.Collapse eventKey="0">
                                                        <Card.Body className='card__body'>
                                                            <div style={{ position: 'relative' }}>
                                                                <div className='font-weight-bold' style={{ marginBottom: '30px' }}>QR Size</div>
                                                                <SliderControl
                                                                    step={2}
                                                                    domain={[1, 7]}
                                                                    enabled={slider.QRSize.enabled}
                                                                    values={[slider.QRSize.value]}
                                                                    tickValues={[1, 3, 5, 7]}
                                                                    onUpdate={(value) => {
                                                                        setSilder({
                                                                            ...slider,
                                                                            QRSize: {
                                                                                ...slider.QRSize,
                                                                                value: value[0]
                                                                            }
                                                                        })
                                                                    }}
                                                                />
                                                            </div>
                                                            <div style={{ marginTop: '90px', position: 'relative' }}>
                                                                <div className='font-weight-bold' style={{ marginBottom: '30px' }}>QR per row</div>
                                                                <SliderControl
                                                                    step={1}
                                                                    domain={[1, 8]}
                                                                    enabled={slider.printSize.enabled}
                                                                    values={[slider.printSize.value]}
                                                                    tickValues={[1, 2, 3, 4, 5, 6, 7, 8]}
                                                                    onUpdate={(value) => {
                                                                        setSilder({
                                                                            ...slider,
                                                                            printSize: {
                                                                                ...slider.printSize,
                                                                                value: value[0]
                                                                            }
                                                                        })
                                                                    }}
                                                                />
                                                            </div>
                                                            <div style={{ marginTop: '90px', position: 'relative' }}>
                                                                <div className='font-weight-bold' style={{ marginBottom: '30px' }}>Space Between QR</div>
                                                                <SliderControl
                                                                    step={1}
                                                                    domain={[1, 7]}
                                                                    enabled={slider.columnSize.enabled}
                                                                    values={[slider.columnSize.value]}
                                                                    tickValues={[1, 2, 3, 4, 5, 6, 7]}
                                                                    onUpdate={(value) => {
                                                                        setSilder({
                                                                            ...slider,
                                                                            columnSize: {
                                                                                ...slider.columnSize,
                                                                                value: value[0]
                                                                            }
                                                                        })
                                                                    }}
                                                                />
                                                            </div>
                                                        </Card.Body>
                                                    </Accordion.Collapse>
                                                </Card>
                                            </Accordion>
                                        </>}
                                        <div style={{ 'position': 'absolute', 'bottom': 0, 'display': 'flex', 'gap': '5px', 'left': 0, 'width': '100%', 'padding': '20px' }} /* className='print-btn__grp' */>
                                            {/* <Dropdown className='ml-auto' disabled={isDisabled}>
                                                    <Dropdown.Toggle>
                                                        <i className="fa fa-cloud-download" /> Download
                                                    </Dropdown.Toggle>

                                                    <Dropdown.Menu>
                                                        <Dropdown.Item onSelect={() => !isDisabled ? generateExcelFile('csv') : null}>CSV</Dropdown.Item>
                                                        <Dropdown.Item onSelect={() => !isDisabled ? generateExcelFile('xl') : null}>XLSX</Dropdown.Item>
                                                    </Dropdown.Menu>
                                                </Dropdown> */}

                                            <Button disabled={isDisabled} style={{ flexBasis: '50%' }} className={`w-50 btn btn-primary btn-sm ${isDisabled ? 'in-active__cursor' : 'active__cursor'}`} onClick={() => generateExcelFile('csv')}>
                                                <i className="fa fa-cloud-download" /> Download CSV</Button>

                                            <ReactToPrint
                                                trigger={() => <Button style={{ flexBasis: '50%' }} className={`w-50 btn btn-success btn-sm ${isDisabled ? 'in-active__cursor' : 'active__cursor'}`} disabled={isDisabled}><i className="fa fa-print" /> Print</Button>}
                                                content={() => componentRef.current}
                                                pageStyle={pageStyle}
                                            />
                                        </div>
                                    </Col>
                                </Row>
                            </Container>
                        </Card.Body>
                    </Card>
                </div>
            </Col>
        </Row >
    )
}